import React, { useEffect, useState, useCallback } from 'react';
import { useDataProvider } from 'react-admin';
import { useMediaQuery, TextField as MuiTextField } from '@material-ui/core';

interface OrderItemsGridProps {
  designCode: string | undefined;
  orderId?: number;
  onChange: (items: { productId: number; qty: number }[]) => void;
  selectedDesignCode?: string | null;
}

interface ProductRecord {
  id: number;
  code?: string;
  productName?: string;
}

interface ItemState {
  productId: number;
  code?: string;
  productName?: string;
  qty: number;
}

const step = 10;

const roundUpToNextTen = (value: number): number => {
  if (value <= 0) return 0;
  return Math.ceil(value / 10) * 10;
};

// ─── Inline Styles ────────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  root: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    marginTop: 12,
  },

  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    borderRadius: 12,
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    border: '1.5px dashed #cbd5e1',
    color: '#64748b',
    fontSize: '0.9rem',
    fontWeight: 500,
    gap: 8,
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: 36,
    marginBottom: 4,
    opacity: 0.5,
  },

  summaryBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px',
    borderRadius: '10px 10px 0 0',
    background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)',
    color: '#f1f5f9',
    fontSize: '0.82rem',
    fontWeight: 600,
    letterSpacing: '0.01em',
    flexWrap: 'wrap' as const,
    gap: 8,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'rgba(255,255,255,0.12)',
    borderRadius: 20,
    padding: '3px 10px',
    fontSize: '0.78rem',
    fontWeight: 600,
  },
  badgeHighlight: {
    color: '#38bdf8',
    fontWeight: 700,
    fontSize: '0.9rem',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    background: '#fff',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
    borderRadius: '0 0 12px 12px',
    overflow: 'hidden',
  },
  thead: {
    background: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
  },
  th: {
    padding: '10px 14px',
    fontWeight: 700,
    fontSize: '0.75rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: '#475569',
    whiteSpace: 'nowrap' as const,
  },
  thCenter: {
    textAlign: 'center' as const,
  },
  td: {
    padding: '8px 14px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '0.85rem',
    color: '#1e293b',
    verticalAlign: 'middle' as const,
  },
  tdCenter: {
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const,
  },
  designChip: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
    color: '#5b21b6',
    borderRadius: 6,
    padding: '2px 10px',
    fontWeight: 700,
    fontSize: '0.78rem',
    letterSpacing: '0.04em',
    border: '1px solid #c4b5fd',
  },
  itemName: {
    color: '#64748b',
    fontWeight: 400,
  },

  qtyWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  btnMinus: {
    minWidth: 32,
    width: 32,
    height: 32,
    borderRadius: 8,
    padding: 0,
    background: '#fee2e2',
    color: '#dc2626',
    fontWeight: 700,
    fontSize: '1rem',
    boxShadow: 'none',
    border: '1.5px solid #fca5a5',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
    flexShrink: 0,
  },
  btnPlus: {
    minWidth: 32,
    width: 32,
    height: 32,
    borderRadius: 8,
    padding: 0,
    background: '#dcfce7',
    color: '#16a34a',
    fontWeight: 700,
    fontSize: '1rem',
    boxShadow: 'none',
    border: '1.5px solid #86efac',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
    flexShrink: 0,
  },

  mobileList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 0,
    background: '#fff',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
    borderRadius: '0 0 12px 12px',
    overflow: 'hidden',
  },
  mobileCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '1px 10px',
    borderBottom: '1px solid #f1f5f9',
    gap: 4,
  },
  mobileCardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
    width: '100%',
  },
  mobileItemInfo: {
    // flex: 1,
    width:'120px',
    minWidth: 0,
    overflow: 'hidden',
    paddingRight: 4,
  },
  mobileQtyInline: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
    flexShrink: 0,
  },
};

// ─── QtyInput sub-component ────────────────────────────────────────────────────
interface QtyInputProps {
  productId: number;
  qty: number;
  inputValues: Map<number, string>;
  setInputValues: React.Dispatch<React.SetStateAction<Map<number, string>>>;
  updateQty: (productId: number, qty: number) => void;
  width?: number;
}

const QtyInput: React.FC<QtyInputProps> = ({
  productId,
  qty,
  inputValues,
  setInputValues,
  updateQty,
  width = 76,
}) => {
  const displayValue = inputValues.has(productId)
    ? inputValues.get(productId)!
    : qty > 0 ? String(qty) : '';

  return (
    <MuiTextField
      style={{ width }}
      variant="outlined"
      size="small"
      type="number"
      inputProps={{ min: 0, style: { textAlign: 'center', fontWeight: 600, fontSize: '0.9rem', padding: '6px 4px' } }}
      value={displayValue}
      onChange={(e) => {
        const val = e.target.value;
        setInputValues((prev) => {
          const next = new Map(prev);
          next.set(productId, val);
          return next;
        });
      }}
      onBlur={(e) => {
        const raw = parseInt(e.target.value || '0', 10) || 0;
        const rounded = roundUpToNextTen(raw);
        updateQty(productId, rounded);
        setInputValues((prev) => {
          const next = new Map(prev);
          next.delete(productId);
          return next;
        });
      }}
    />
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const OrderItemsGridComponent: React.FC<OrderItemsGridProps> = ({
  designCode,
  orderId,
  onChange,
  selectedDesignCode,
}) => {
  const dataProvider = useDataProvider();
  const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  const [items, setItems] = useState<ItemState[]>([]);
  const [inputValues, setInputValues] = useState<Map<number, string>>(new Map());
  const [qtyMap, setQtyMap] = useState<Map<number, number>>(new Map()); // ← Added back
  const qtyMapRef = React.useRef(new Map<number, number>());
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  // ── Load products ────────────────────────────────────────────────────────────
  const loadProducts = useCallback(
    async (pageToLoad: number, append: boolean) => {
      if (!designCode) {
        setItems([]);
        onChange([]);
        return;
      }
      setLoading(true);

      try {
        const result: any = await dataProvider.getList('products', {
          pagination: { page: pageToLoad, perPage: 10000 },
          sort: { field: 'productName', order: 'ASC' },
          filter: { groupId: designCode },
        });

        const rows: ProductRecord[] = result?.data ?? [];
        let pageItems: ItemState[] = rows.map((p) => ({
          productId: p.id,
          code: p.code,
          productName: p.productName,
          qty: 0,
        }));

        pageItems.forEach((it) => {
          const q = qtyMapRef.current.get(it.productId);
          if (q != null) it.qty = q;
        });

        setItems((prev) => {
          const next = append ? [...prev, ...pageItems] : pageItems;
          onChange(next.filter((it) => it.qty > 0).map((it) => ({ productId: it.productId, qty: it.qty })));
          return next;
        });
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [designCode, onChange, dataProvider]
  );

  // ── Load existing order items ─────────────────────────────────────────────
  useEffect(() => {
    const loadExisting = async () => {
      if (orderId) {
        try {
          const existing: any = await dataProvider.getList('order-items', {
            pagination: { page: 1, perPage: 100000 },
            sort: { field: 'id', order: 'ASC' },
            filter: { orderId },
          });

          const newQtyMap = new Map<number, number>();
          for (const row of existing.data) {
            newQtyMap.set(row.productId, row.qty || 0);
          }
          setQtyMap(newQtyMap);
          qtyMapRef.current = newQtyMap;

          const itemsArray: ItemState[] = existing.data.map((row: any) => ({
            productId: row.productId,
            code: row.code,
            productName: row.productName,
            qty: row.qty || 0,
          }));

          const sorted = [...itemsArray].sort((a, b) => {
            if (a.qty > 0 && b.qty === 0) return -1;
            if (a.qty === 0 && b.qty > 0) return 1;
            return (a.productName ?? '').localeCompare(b.productName ?? '');
          });

          setItems(sorted);
          onChange(sorted.filter((it) => it.qty > 0).map((it) => ({ productId: it.productId, qty: it.qty })));
        } catch {
          setQtyMap(new Map());
          qtyMapRef.current = new Map();
        }
      } else {
        setQtyMap(new Map());
        qtyMapRef.current = new Map();
      }
    };
    loadExisting();
  }, [orderId, dataProvider, onChange]);

  useEffect(() => {
    loadProducts(1, false);
  }, [loadProducts]);

  // ── Update qty ───────────────────────────────────────────────────────────────
  const updateQty = (productId: number, newQty: number) => {
    if (newQty < 0) newQty = 0;
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    setItems((prev) => {
      const updated = prev.map((it) =>
        it.productId === productId ? { ...it, qty: newQty } : it
      );

      // Fixed: Using setQtyMap here
      setQtyMap((prevMap) => {
        const next = new Map(prevMap);
        if (newQty > 0) next.set(productId, newQty);
        else next.delete(productId);
        qtyMapRef.current = next;
        return next;
      });

      onChange(updated.filter((it) => it.qty > 0).map((it) => ({ productId: it.productId, qty: it.qty })));
      return updated;
    });

    setTimeout(() => window.scrollTo(scrollX, scrollY), 0);
  };

  const handlePlus = (productId: number) => {
    const current = items.find((it) => it.productId === productId)?.qty || 0;
    updateQty(productId, current + step);
  };

  const handleMinus = (productId: number) => {
    const current = items.find((it) => it.productId === productId)?.qty || 0;
    updateQty(productId, Math.max(0, current - step));
  };

  const totalQty = Array.from(qtyMapRef.current.values()).reduce((sum, q) => sum + q, 0);
  const totalProducts = Array.from(qtyMapRef.current.values()).filter((q) => q > 0).length;

  const filteredItems = React.useMemo(() => {
    if (!searchText.trim()) return items;
    const term = searchText.toLowerCase();
    return items.filter(item => (item.productName || '').toLowerCase().includes(term));
  }, [items, searchText]);

  if (!designCode) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyIcon}>🎨</div>
        <div>Please select a Design to view items.</div>
      </div>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyIcon}>📦</div>
        <div>No items found for design <strong>{designCode}</strong>.</div>
      </div>
    );
  }

  // Mobile View
  if (isSmall) {
    return (
      <div style={styles.root}>
        <div style={styles.summaryBar}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={styles.badge}>Products <span style={styles.badgeHighlight}>{totalProducts}</span></span>
            <span style={styles.badge}>Total Qty <span style={styles.badgeHighlight}>{totalQty}</span></span>
          </div>
          <MuiTextField
            size="small"
            variant="outlined"
            placeholder="Search Item Name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            autoComplete="off"
            // style={{ background: '#fff', borderRadius: 6, minWidth: 250 }}
            style={{
              width: '100%',
              maxWidth: '100%',
              background: '#fff',
              borderRadius: 6,
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={styles.mobileList}>
          {filteredItems.map((item, idx) => (
            <div
              key={item.productId}
              style={{
                ...styles.mobileCard,
                background: item.qty > 0 ? '#f0fdf4' : idx % 2 === 0 ? '#fff' : '#fafafa',
                borderLeft: item.qty > 0 ? '3px solid #22c55e' : '3px solid transparent',
              }}
            >
              <div style={styles.mobileCardTop}>
                <div style={styles.mobileItemInfo}>
                  <div style={{ fontWeight: 600, fontSize: '0.75rem', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.productName}
                  </div>
                </div>
                <div style={styles.mobileQtyInline}>
                  <button type="button" style={styles.btnMinus} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMinus(item.productId); }}>−</button>
                  <QtyInput productId={item.productId} qty={item.qty} inputValues={inputValues} setInputValues={setInputValues} updateQty={updateQty} width={58} />
                  <button type="button" style={styles.btnPlus} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePlus(item.productId); }}>+</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Desktop View
  return (
    <div style={styles.root}>
      <div style={styles.summaryBar}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span style={styles.badge}>Products <span style={styles.badgeHighlight}>{totalProducts}</span></span>
          <span style={styles.badge}>Total Qty <span style={styles.badgeHighlight}>{totalQty}</span></span>
        </div>
        <MuiTextField
          size="small"
          variant="outlined"
          placeholder="Search Item Name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          autoComplete="off"
          style={{ background: '#fff', borderRadius: 6, minWidth: 250 }}
        />
      </div>

      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '500px', border: '1px solid #e2e8f0', borderTop: 'none' }}>
        <table style={styles.table}>
          <thead style={{ ...styles.thead, position: 'sticky', top: 0, zIndex: 10, background: '#f8fafc' }}>
            <tr>
              <th style={{ ...styles.th, width: 120 }}>Design No.</th>
              <th style={styles.th}>Item Name</th>
              <th style={{ ...styles.th, ...styles.thCenter, width: 200 }}>Quantity</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item, idx) => (
              <tr
                key={item.productId}
                style={{
                  background: item.qty > 0 ? '#f0fdf4' : idx % 2 === 0 ? '#fff' : '#fafafa',
                  borderLeft: item.qty > 0 ? '3px solid #22c55e' : '3px solid transparent',
                }}
              >
                <td style={styles.td}>
                  <span style={styles.designChip}>{selectedDesignCode ?? designCode}</span>
                </td>
                <td style={styles.td}>
                  <span style={styles.itemName}>{item.productName}</span>
                </td>
                <td style={{ ...styles.td, ...styles.tdCenter }}>
                  <div style={styles.qtyWrapper}>
                    <button type="button" style={styles.btnMinus} onClick={(e) => { e.preventDefault(); handleMinus(item.productId); }}>−</button>
                    <QtyInput productId={item.productId} qty={item.qty} inputValues={inputValues} setInputValues={setInputValues} updateQty={updateQty} width={76} />
                    <button type="button" style={styles.btnPlus} onClick={(e) => { e.preventDefault(); handlePlus(item.productId); }}>+</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const OrderItemsGrid = React.memo(OrderItemsGridComponent);